import { ReelsContainerHelper } from '@/containers/reels/ReelsContainerHelper'
import { ReelsStripConfig } from '@/containers/reels/ReelsStripConfig'
import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolContainer } from '@/containers/symbols/SymbolContainer'
import { SymbolLayerType } from '@/containers/symbols/SymbolLayerType'
import { SymbolsTypeHelper } from '@/containers/symbols/SymbolsTypeHelper'
import { SymbolType } from '@/containers/symbols/SymbolType'
import Facade from '@/Facade'
import { SymbolsPool } from '@/factories/Factory'
import { IReelsTimingsConfig, NewSymbolData, ReelData, SymbolContainerData, SymbolData } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import { HOLD_AND_WIN_RANDOM_SPIN_MULTIPLIERS } from '@/utils/HoldAndWinCoinValue'
import gsap from 'gsap'
import * as PIXI from 'pixi.js'
import type { TrackEntry as ITrackEntry } from '@esotericsoftware/spine-pixi-v8'

export class ReelContainer {
    private static readonly MIN_DURATION_SECONDS = 0.016
    private static readonly SUSPENSE_END_SLOWDOWN_MULTIPLIER = 4.2
    private static readonly SUSPENSE_END_EASING = 'none'
    // Continuous GSAP-animated deceleration: duration and target fraction
    private static readonly SUSPENSE_DECEL_DURATION = 2.2
    private static readonly SUSPENSE_DECEL_TARGET_FRACTION = 0.12
    private static readonly SUSPENSE_FINAL_STOP_MIN_RATIO = 1.15
    private static readonly SUSPENSE_BOUNCE_SHIFT_MULTIPLIER = 2.2
    private static readonly SUSPENSE_LAST_SYMBOL_EXTRA_DROP = 34
    private static readonly SUSPENSE_BOUNCE_BACK_TIME_MULTIPLIER = 1.55
    private static readonly SUSPENSE_BOUNCE_BACK_EASING = 'power1.out'
    private static readonly SPIN_COIN_TYPES = ['prize']
    private static readonly SPIN_COIN_MULTIPLIERS = HOLD_AND_WIN_RANDOM_SPIN_MULTIPLIERS

    private static normalizeCoinAmount(value: number): number {
        if (!Number.isFinite(value)) {
            return 0
        }

        return Math.round((value + Number.EPSILON) * 100) / 100
    }

    protected readonly _symbolHeight: number
    protected readonly _symbolsCount: number
    protected _stripIndex: number
    protected _speedSettings: IReelsTimingsConfig
    protected _reelsStrip: number[]
    protected _winPositions: number[]
    protected _pendingSuspenseStop: boolean
    protected _suspenseCurrentSpeed: number | null
    protected _suspenseLastCycleDuration: number
    protected _anticipationDimmingActive: boolean
    protected _anticipationDimPower: number
    protected _anticipationHighlightedSymbolIndex: number | null
    private _suspenseDecelerationTween: gsap.core.Tween | null
    private _suspenseStopReady: boolean
    protected _duration: number
    protected _useBlurSymbolTextures: boolean
    private rect: PIXI.Graphics

    public reelGrid: number[]
    public originalReelGrid: SymbolData[]
    public symbols: SymbolContainer[]
    public symbolLayers: PIXI.Container[]
    public index: number
    public reelStates = {
        BEFORE_START: 'BEFORE_START',
        STARTING: 'STARTING',
        BEFORE_STOPPING: 'BEFORE_STOPPING',
        STOPPING: 'STOPPING',
        STOPPED: 'STOPPED',
        CASCADING: 'CASCADING',
        SUSPENSE_CASCADING: 'SUSPENSE_CASCADING',
    }
    public reelState: string

    public constructor(data: ReelData) {
        this.index = data.reelIndex
        this.symbolLayers = []
        this.symbols = []
        this._stripIndex = 0
        this._symbolHeight = ReelsContainerHelper.calculateReelHeight()
        this._symbolsCount = data.reelGrid.length
        this._pendingSuspenseStop = false
        this._suspenseCurrentSpeed = null
        this._suspenseLastCycleDuration = 0
        this._anticipationDimmingActive = false
        this._anticipationDimPower = 0.55
        this._anticipationHighlightedSymbolIndex = null
        this._suspenseDecelerationTween = null
        this._suspenseStopReady = false
        this._duration = 0
        this._useBlurSymbolTextures = false
        this.setState(this.reelStates.BEFORE_START)

        this.selectReelsStrip(data.activeReelStrip)
        this.changeSpinSettings(Facade.isTurboSpin)
        this.createSymbolsLayers()
        this.initSymbols(data)
        this.updateSymbolsAfterStop()
    }

    public selectReelsStrip(reelsStrip: number[]) {
        this._reelsStrip = reelsStrip
        this._stripIndex = this._stripIndex % reelsStrip.length
    }

    public updateHiddenSymbols = (grid: SymbolData[], reelHiddenPositions: number[][]) => {
        /**Update new symbols from server**/
        this._winPositions = reelHiddenPositions.map((position: number[]) => {
            return position[1]
        })
        const updatedSymbolsArray: SymbolContainer[] = []

        this._winPositions.forEach((y: number, index: number, array: number[]) => {
            const symbol: SymbolContainer = this.symbols[y]
            updatedSymbolsArray.push(symbol)
            symbol.y = -this._symbolHeight * (array.length - index) - 33
            symbol.init({
                symbolData: {
                    index: grid[index].id,
                    multiplier: grid[index].multiplier,
                },
                allowUnmasked: false,
            })
            this.changeSymbolLayer(symbol)
        })

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            if (!this._winPositions.includes(index)) {
                updatedSymbolsArray.push(symbol)
            }
        })

        this.symbols = updatedSymbolsArray
    }

    public suspenseSpinStage1 = (): number => {
        this.setState(this.reelStates.SUSPENSE_CASCADING)
        const newNonWinningsSymbols: { symbol: SymbolContainer; index: number }[] = []

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            if (symbol.y !== this._symbolHeight * index && symbol.y >= 0) {
                newNonWinningsSymbols.push({ symbol, index })
            }
        })

        let delay: number = 0
        const duration: number =
            (Facade.anticipationConfig.NON_WINNINGS_SYMBOLS_STOPPING_SPEED /
                Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL) *
            this._winPositions.length
        let finalDelay: number = 0

        newNonWinningsSymbols.forEach((data: { symbol: SymbolContainer; index: number }, _index: number) => {
            const symbol: SymbolContainer = data.symbol
            delay =
                Facade.anticipationConfig.DELAY_PER_NON_WINNINGS_REEL_SYMBOL * (newNonWinningsSymbols.length - _index)

            gsap.to(symbol, {
                delay,
                duration,
                y: data.index * this._symbolHeight + Facade.anticipationConfig.NON_WINNINGS_BOUNCING_SHIFT,
                ease: this._speedSettings.LAST_EASING,
                onComplete: () => {
                    if (Facade.anticipationConfig.NON_WINNINGS_BOUNCING_SHIFT) {
                        const tl = gsap.timeline()
                        tl.to(symbol, {
                            duration: Facade.anticipationConfig.NON_WINNINGS_BOUNCING_BACK_TIME,
                            y: `-=${Facade.anticipationConfig.NON_WINNINGS_BOUNCING_SHIFT}`,
                            ease: this._speedSettings.BOUNCE_EASING,
                            onComplete: () => {
                                this.changeSymbolLayer(symbol, true)
                            },
                            onUpdate: () => {
                                if (tl.progress() >= 0.8) {
                                    this.updateSymbolAfterStop(symbol)
                                }
                            },
                        })
                    }

                },
            })

            if (_index === newNonWinningsSymbols.length - 1) {
                finalDelay = delay + duration + Facade.anticipationConfig.NON_WINNINGS_BOUNCING_BACK_TIME
            }
        })

        return finalDelay
    }

    public suspenseSpinStage2 = (isLastCascadeReel: boolean) => {
        const winningsSymbols: SymbolContainer[] = []
        const winningsSymbolsIndexes: number[] = []

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            if (symbol.y !== this._symbolHeight * index && symbol.y < 0) {
                winningsSymbols.push(symbol)
                winningsSymbolsIndexes.push(index)
            }
        })

        winningsSymbols.forEach((symbol: SymbolContainer, index: number) => {
            const duration: number =
                (Facade.anticipationConfig.WINNINGS_SYMBOLS_STOPPING_SPEED /
                    Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL) *
                winningsSymbols.length

            gsap.to(symbol, {
                delay: Facade.anticipationConfig.DELAY_PER_WINNINGS_REEL_SYMBOL * (winningsSymbols.length - index),
                duration,
                y:
                    winningsSymbolsIndexes[index] * this._symbolHeight +
                    Facade.anticipationConfig.WINNINGS_BOUNCING_SHIFT,
                ease: this._speedSettings.LAST_EASING,
                onComplete: () => {
                    if (index === 0) {
                        this.setState(this.reelStates.STOPPED)
                        GameSignals.reelJustTouched.emit(this.index, winningsSymbolsIndexes)
                    }
                    if (Facade.anticipationConfig.WINNINGS_BOUNCING_SHIFT) {
                        const tl = gsap.timeline()
                        tl.to(symbol, {
                            duration: Facade.anticipationConfig.WINNINGS_BOUNCING_BACK_TIME,
                            y: `-=${Facade.anticipationConfig.WINNINGS_BOUNCING_SHIFT}`,
                            ease: this._speedSettings.BOUNCE_EASING,
                            onComplete: () => {
                                this.changeSymbolLayer(symbol, true)
                                if (index === 0 && isLastCascadeReel) {
                                    gsap.to(this, {
                                        duration: 0.5,
                                        onComplete: () => {
                                            GameSignals.completeCascadeSpin.emit()
                                        },
                                    })
                                }
                            },
                            onUpdate: () => {
                                if (tl.progress() >= 0.8) {
                                    this.updateSymbolAfterStop(symbol)
                                }
                            },
                        })
                    }

                },
            })
        })

        if (!winningsSymbols.length && isLastCascadeReel) {
            this.setState(this.reelStates.STOPPED)
            GameSignals.reelJustTouched.emit(this.index, winningsSymbolsIndexes)
            gsap.to(this, {
                duration: 0.05,
                onComplete: () => {
                    GameSignals.completeCascadeSpin.emit()
                },
            })
        }
    }

    public cascadeSpin = (isLastCascadeReel: boolean) => {
        this.setState(this.reelStates.CASCADING)
        const winningsSymbolsIndexes: number[] = []
        let movedSymbolsCount: number = 0

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            if (symbol.y !== this._symbolHeight * index && symbol.y < 0) {
                winningsSymbolsIndexes.push(index)
            }
        })

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            const y: number = this._symbolHeight * index
            const isNewSymbol: boolean = symbol.y !== y
            const duration: number =
                (this._speedSettings.STOPPING_SPEED / Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL / 1000) *
                this._winPositions.length

            if (isNewSymbol) {
                movedSymbolsCount++
                gsap.to(symbol, {
                    delay: this._speedSettings.DELAY_PER_REEL_SYMBOL * (this.symbols.length - index),
                    duration,
                    y: index * this._symbolHeight + this._speedSettings.BOUNCING_SHIFT,
                    ease: this._speedSettings.LAST_EASING,
                    onComplete: () => {
                        if (index === 0) {
                            this.setState(this.reelStates.STOPPED)
                            GameSignals.reelJustTouched.emit(this.index, winningsSymbolsIndexes)
                        }
                        if (this._speedSettings.BOUNCING_SHIFT) {
                            const tl = gsap.timeline()
                            tl.to(symbol, {
                                duration: this._speedSettings.BOUNCING_BACK_TIME,
                                y: `-=${this._speedSettings.BOUNCING_SHIFT}`,
                                ease: this._speedSettings.BOUNCE_EASING,
                                onComplete: () => {
                                    this.changeSymbolLayer(symbol, true)
                                    if (index === 0 && isLastCascadeReel) {
                                        gsap.to(this, {
                                            duration: 0.5,
                                            onComplete: () => {
                                                GameSignals.completeCascadeSpin.emit()
                                            },
                                        })
                                    }
                                },
                                onUpdate: () => {
                                    if (tl.progress() >= 0.8) {
                                        this.updateSymbolAfterStop(symbol)
                                    }
                                },
                            })
                        }

                    },
                })
            }
        })

        if (!movedSymbolsCount && isLastCascadeReel) {
            this.setState(this.reelStates.STOPPED)
            GameSignals.reelJustTouched.emit(this.index, winningsSymbolsIndexes)
            gsap.to(this, {
                duration: 0.05,
                onComplete: () => {
                    GameSignals.completeCascadeSpin.emit()
                },
            })
        }
    }

    public start() {
        this.setState(this.reelStates.BEFORE_START)
        this._pendingSuspenseStop = false
        this._suspenseCurrentSpeed = null
        this._suspenseLastCycleDuration = 0
        this._suspenseStopReady = false
        this.changeSpinSettings(Facade.isTurboSpin)
        this._duration = this.calculateDuration(this._symbolHeight, this.getRegularSpinSpeed())
        this.resetSymbols()
        this.enableLayersBlur()
        this.setState(this.reelStates.STARTING)
        this.spinSymbols(
            this.getRandomNonSpecialIndex(),
            this.calculateDuration(this._symbolHeight, this.getFirstSpinSpeed()),
            this._speedSettings.FIRST_EASING
        )
    }

    public setData = (reelGrid: number[], originalGrid: SymbolData[], isQuickStop: boolean = false) => {
        this.changeSpinSettings(isQuickStop)
        this.reelGrid = reelGrid
        this.originalReelGrid = originalGrid
    }

    /** Rebuilds the reel on the given grid without any spin/stop animation. */
    public applyStaticGrid = (reelGrid: number[], originalGrid: SymbolData[]) => {
        if (!Array.isArray(reelGrid) || !reelGrid.length) {
            return
        }

        gsap.killTweensOf(this.symbols)

        if (this._suspenseDecelerationTween) {
            this._suspenseDecelerationTween.kill()
            this._suspenseDecelerationTween = null
        }

        this._pendingSuspenseStop = false
        this._suspenseCurrentSpeed = null
        this._suspenseLastCycleDuration = 0
        this._suspenseStopReady = false
        this._winPositions = []

        this.disableLayersBlur()

        this.symbols.forEach((symbol: SymbolContainer) => {
            gsap.killTweensOf(symbol)
            symbol.stopAnimation()
        })

        while (this.symbols.length) {
            SymbolsPool.in(this.symbols.pop())
        }

        this.reelGrid = [...reelGrid]
        this.originalReelGrid = Array.isArray(originalGrid) ? [...originalGrid] : []

        this.initSymbols({
            reelIndex: this.index,
            reelGrid: this.reelGrid,
            originalGrid: this.originalReelGrid,
            activeReelStrip: this._reelsStrip,
        })
        this.updateSymbolsAfterStop()
        this.setState(this.reelStates.STOPPED)
    }

    public setAnticipationDimming(
        active: boolean,
        dimPower: number = 0.55,
        highlightedSymbolIndex: number | null = null
    ): void {
        this._anticipationDimmingActive = active
        this._anticipationDimPower = dimPower
        this._anticipationHighlightedSymbolIndex = highlightedSymbolIndex
    }

    public stoppingReel = (isSuspenseReel: boolean, immediately: boolean) => {
        const beginStop = (suspenseMode: boolean) => {
            const hasStopData = Array.isArray(this.reelGrid) && Array.isArray(this.originalReelGrid)

            if (!hasStopData) {
                this._pendingSuspenseStop = suspenseMode
                this.setState(this.reelStates.BEFORE_STOPPING)
                return
            }

            this.setState(this.reelStates.STOPPING)
            this._pendingSuspenseStop = false
            this.disableLayersBlur()
            this.resetSymbolsPositions()
            this.addNewServerSymbols()
            this.stoppingSpinSymbols(suspenseMode)
        }

        if (immediately) {
            this.symbols.forEach((symbol: SymbolContainer) => {
                gsap.killTweensOf(symbol)
                symbol.y = this._symbolHeight * Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL + symbol.y
            })
            beginStop(isSuspenseReel)
            return
        }

        if (this.reelState === this.reelStates.STOPPING || this.reelState === this.reelStates.STOPPED) {
            return
        }

        if (this.reelState === this.reelStates.BEFORE_STOPPING) {
            beginStop(this._pendingSuspenseStop)
            return
        }

        this._pendingSuspenseStop = isSuspenseReel
        this._suspenseStopReady = false
        if (isSuspenseReel) {
            const baseSpeed = this.getRegularSpinSpeed()
            this._suspenseCurrentSpeed = baseSpeed
            this._suspenseLastCycleDuration = this.calculateDuration(this._symbolHeight, baseSpeed)
            if (this._suspenseDecelerationTween) {
                this._suspenseDecelerationTween.kill()
            }
            this._suspenseDecelerationTween = gsap.to(this, {
                _suspenseCurrentSpeed: baseSpeed * ReelContainer.SUSPENSE_DECEL_TARGET_FRACTION,
                duration: ReelContainer.SUSPENSE_DECEL_DURATION,
                ease: 'power2.in',
                onComplete: () => {
                    this._suspenseStopReady = true
                },
            })
        } else {
            this._suspenseCurrentSpeed = null
            this._suspenseLastCycleDuration = 0
            if (this._suspenseDecelerationTween) {
                this._suspenseDecelerationTween.kill()
                this._suspenseDecelerationTween = null
            }
        }
        this.setState(this.reelStates.BEFORE_STOPPING)
    }

    protected createSymbol(data: NewSymbolData): SymbolContainer {
        const layerIndex = this.getSymbolLayerIndex(data.symbolData.index, data.allowUnmasked)
        return SymbolsPool.out(this.symbolLayers[layerIndex], data)
    }

    protected createSymbolsLayers() {
        const layersCount = SymbolLayerType.COUNT

        for (let index = 0; index < layersCount; index++) {
            const layer = new PIXI.Container()
            layer.name = `Reel #${this.index + 1}`
            layer.x = Facade.symbolsConfig.WIDTH / 2
            layer.y = this._symbolHeight / 2

            this.symbolLayers.push(layer)
        }
    }

    protected initSymbols(data: ReelData) {
        const { EXTRA_SYMBOLS_TOP, EXTRA_SYMBOLS_BOTTOM } = Facade.reelsConfig

        let symbols = [...data.reelGrid]

        for (let index = 0; index < EXTRA_SYMBOLS_TOP; index++) {
            symbols.unshift(this.getRandomNonSpecialIndex())
        }

        for (let index = 0; index < EXTRA_SYMBOLS_BOTTOM; index++) {
            symbols.push(this.getRandomNonSpecialIndex())
        }

        for (let index = 0; index < symbols.length; index++) {
            const originalGridIndex = index - EXTRA_SYMBOLS_TOP
            const isVisibleReelSymbol = index >= EXTRA_SYMBOLS_TOP && index < symbols.length - EXTRA_SYMBOLS_BOTTOM
            const symbolData: SymbolContainerData = this.getDefaultSymbolData(
                symbols[index],
                data.originalGrid[originalGridIndex]?.multiplier ?? 0,
                data.originalGrid[originalGridIndex]
            )
            const symbol = this.createSymbol({
                symbolData,
                allowUnmasked: isVisibleReelSymbol,
            })

            symbol.y = this._symbolHeight * (index - EXTRA_SYMBOLS_TOP)
            symbol.name = '' + symbols[index]
            this.symbols.push(symbol)
        }
    }
    protected onClick(symbol: any) {
        symbol.scale.set(1.3)
    }
    protected addNewServerSymbols() {
        const botRandomData = this.getDefaultSymbolData(this.getRandomNonSpecialIndex(), 0)
        this.addSymbolOnTop(
            this.createSymbol({
                symbolData: botRandomData,
                allowUnmasked: false,
            })
        )

        for (let index = this.reelGrid.length - 1; index >= 0; index--) {
            const symbolData = this.getDefaultSymbolData(
                this.reelGrid[index],
                this.originalReelGrid[index]?.multiplier ?? 0,
                this.originalReelGrid[index]
            )

            try {
                this.addSymbolOnTop(
                    this.createSymbol({
                        symbolData,
                        allowUnmasked: false,
                    })
                )
            } catch (error) {}
        }

        const topRandomData = this.getDefaultSymbolData(this.getRandomNonSpecialIndex(), 0)
        this.addSymbolOnTop(
            this.createSymbol({
                symbolData: topRandomData,
                allowUnmasked: false,
            })
        )
    }

    protected addSymbolOnTop(symbol: SymbolContainer) {
        symbol.y = this.symbols[0].y - this._symbolHeight

        if (this._useBlurSymbolTextures) {
            symbol.enableBlurStaticTexture()
        } else {
            symbol.disableBlurStaticTexture()
        }

        this.changeSymbolLayer(symbol, false, true)
        this.applyAnticipationDimmingToSymbol(symbol)
        this.symbols.unshift(symbol)
    }

    protected applyAnticipationDimmingToSymbol(symbol: SymbolContainer): void {
        if (!symbol) {
            return
        }

        if (!this._anticipationDimmingActive) {
            return
        }

        if (
            symbol.index === SpecialSymbolType.MULTIPLIER ||
            symbol.index === SpecialSymbolType.SCATTER ||
            (this._anticipationHighlightedSymbolIndex !== null && symbol.index === this._anticipationHighlightedSymbolIndex)
        ) {
            symbol.shadowOut(false)
            return
        }

        symbol.shadowIn(false, this._anticipationDimPower)
    }

    protected getDefaultSymbolData(index: number, multiplier: number, gridEntry?: any): SymbolContainerData {
        const isRandomSpinCoin = index === SpecialSymbolType.MULTIPLIER && !gridEntry
        const selectedBetRaw = Number(Facade.model?.currentBet ?? 0)
        const selectedStake =
            Number.isFinite(selectedBetRaw) && selectedBetRaw > 0
                ? toMajorUnits(selectedBetRaw)
                : Number(Facade.model?.currentCoinBet ?? 1)
        const rawCoinValue = Number(gridEntry?.coinValue ?? gridEntry?.coin_value)
        const toStakeCoinValue = (credit: number): number => {
            const safeCredit = Number.isFinite(credit) && credit > 0 ? credit : 0
            const safeStake = Number.isFinite(selectedStake) && selectedStake > 0 ? selectedStake : 1
            return ReelContainer.normalizeCoinAmount(safeCredit * safeStake)
        }

        return {
            index,
            multiplier,
                        ...(gridEntry?.coinType
                ? { coinType: String(gridEntry.coinType) }
                : isRandomSpinCoin
                  ? { coinType: ReelContainer.pickRandomSpinCoinType() }
                  : {}),
                        ...(Number.isFinite(rawCoinValue) && rawCoinValue > 0
                                ? { coinValue: toStakeCoinValue(rawCoinValue) }
                : isRandomSpinCoin
                  ? { coinValue: ReelContainer.pickRandomSpinCoinValue() }
                  : {}),
            ...(gridEntry?.symbolKey ? { symbolKey: String(gridEntry.symbolKey) } : {}),
        }
    }

    private static pickRandomSpinCoinType(): string {
        return ReelContainer.SPIN_COIN_TYPES[
            Math.floor(Math.random() * ReelContainer.SPIN_COIN_TYPES.length)
        ]
    }

    private static pickRandomSpinCoinValue(): number {
        const mult = ReelContainer.SPIN_COIN_MULTIPLIERS[
            Math.floor(Math.random() * ReelContainer.SPIN_COIN_MULTIPLIERS.length)
        ]
        const currentBetRaw = Number(Facade.model?.currentBet ?? 0)
        const bet =
            Number.isFinite(currentBetRaw) && currentBetRaw > 0
                ? toMajorUnits(currentBetRaw)
                : Facade.model?.currentCoinBet ?? 1
        return ReelContainer.normalizeCoinAmount(mult * bet)
    }

    protected getRandomIndex(): number {
        const randomIndex = ReelsStripConfig.REELS_SYMBOLS_INDEX + this._reelsStrip[this._stripIndex]
        this._stripIndex = (this._stripIndex + 1) % this._reelsStrip.length
        return randomIndex
    }

    protected getRandomNonSpecialIndex(): number {
        const randomIndex = this.getRandomIndex()

        if (randomIndex >= SpecialSymbolType.SCATTER) {
            // replacing special symbols, on adjacent rows they look awful

            const symbolsCount = SymbolType.HP_4 - SymbolType.LP_1
            const randomOffset = Math.floor(Math.random() * symbolsCount)
            return SymbolType.LP_1 + randomOffset
        }

        return randomIndex
    }

    protected getSymbolLayerIndex(index: number, allowUnmasked: boolean = false): number {
        if (SymbolsTypeHelper.isSpecialSymbol(index)) {
            if (allowUnmasked) {
                switch (index) {
                    case SpecialSymbolType.MULTIPLIER:
                        return SymbolLayerType.UNMASKED_SPECIAL_WILD_SYMBOLS
                    case SpecialSymbolType.SCATTER:
                        return SymbolLayerType.UNMASKED_SPECIAL_SCATTER_SYMBOLS
                    default:
                        return SymbolLayerType.UNMASKED_SPECIAL_SYMBOLS
                }
            }

            switch (index) {
                case SpecialSymbolType.SCATTER:
                    return SymbolLayerType.SPECIAL_SCATTER_SYMBOLS
                default:
                    return SymbolLayerType.SPECIAL_SYMBOLS
            }
        }

        if (allowUnmasked) {
            return SymbolLayerType.UNMASKED_STANDARD_SYMBOLS
        } else {
            return SymbolLayerType.STANDARD_SYMBOLS
        }
    }

    protected changeSpinSettings(isQuickSpin: boolean) {
        this._speedSettings = isQuickSpin ? Facade.reelsConfig.QUICK : Facade.reelsConfig.NORMAL
    }

    public changeSymbolLayer(symbol: SymbolContainer, allowUnmasked: boolean = false, forceSpawnBot: boolean = false) {
        const layerIndex = this.getSymbolLayerIndex(symbol.index, allowUnmasked)
        const layer = this.symbolLayers[layerIndex]

        if (forceSpawnBot) {
            layer.addChildAt(symbol, 0)
        } else {
            if (symbol.parent !== layer) {
                layer.addChild(symbol)
            }
        }
    }

    protected setSymbolLayer(symbol: SymbolContainer, layerIndex: number) {
        const layer = this.symbolLayers[layerIndex]

        if (symbol.parent !== layer) {
            layer.addChild(symbol)
        }
    }

    protected calculateDuration(distance: number, speed: number): number {
        if (!speed || speed <= 0) {
            return ReelContainer.MIN_DURATION_SECONDS
        }

        return Math.max(distance / speed, ReelContainer.MIN_DURATION_SECONDS)
    }

    protected getFirstSpinSpeed(): number {
        const speedSettings = this._speedSettings as unknown as Record<string, number | undefined>
        const firstSpeed = speedSettings['FIRST_SPEED']
        return firstSpeed || this._speedSettings.REGULAR_SPEED || this._speedSettings.STOPPING_SPEED
    }

    protected getRegularSpinSpeed(): number {
        const speedSettings = this._speedSettings as unknown as Record<string, number | undefined>
        const firstSpeed = speedSettings['FIRST_SPEED']
        return this._speedSettings.REGULAR_SPEED || firstSpeed || this._speedSettings.STOPPING_SPEED
    }

    protected getLastSpinSpeed(): number {
        const speedSettings = this._speedSettings as unknown as Record<string, number | undefined>
        const lastSpeed = speedSettings['LAST_SPEED']
        return lastSpeed || this._speedSettings.STOPPING_SPEED || this._speedSettings.REGULAR_SPEED
    }

    protected spinSymbols(index: number, duration: number, easing: string) {
        gsap.to(this.symbols, {
            duration,
            y: `+=${this._symbolHeight}`,
            ease: easing,
            onComplete: () => {
                const symbol = this.symbols.pop()

                if (!symbol) {
                    return
                }

                symbol.init({
                    symbolData: this.getDefaultSymbolData(index, 0),
                    allowUnmasked: false,
                })
                this.addSymbolOnTop(symbol)

                if (this.reelState === this.reelStates.BEFORE_STOPPING) {
                    if (this._suspenseStopReady) {
                        this._suspenseStopReady = false
                        this.stoppingReel(this._pendingSuspenseStop, false)
                        return
                    }
                    if (this._suspenseCurrentSpeed !== null) {
                        // Read GSAP-interpolated speed this frame — no step change, fully smooth
                        const cycleDuration = this.calculateDuration(this._symbolHeight, this._suspenseCurrentSpeed)
                        this._suspenseLastCycleDuration = cycleDuration
                        this.spinSymbols(this.getRandomNonSpecialIndex(), cycleDuration, 'none')
                        return
                    }
                    this.stoppingReel(this._pendingSuspenseStop, false)
                    return
                }

                this.spinSymbols(this.getRandomNonSpecialIndex(), this._duration, 'none')
            },
        })
    }

    protected resetSymbolsPositions = () => {
        let y: number = -Facade.reelsConfig.EXTRA_SYMBOLS_TOP * this._symbolHeight

        this.symbols.forEach((symbol: SymbolContainer) => {
            symbol.y = y
            y += this._symbolHeight
        })
    }

    protected stoppingSpinSymbols(isSuspenseReel: boolean) {
        this.disableLayersBlur()

        const baseBounceShift = this._speedSettings.BOUNCING_SHIFT || 0
        const suspenseBounceShift = isSuspenseReel
            ? baseBounceShift * ReelContainer.SUSPENSE_BOUNCE_SHIFT_MULTIPLIER
            : baseBounceShift

        const onSpinStop = () => setTimeout(this.onSpinStop, 100)
        const stopDistance = Math.abs(this._symbolHeight + suspenseBounceShift)
        const baseDuration = this.calculateDuration(stopDistance, this.getLastSpinSpeed())
        const incomingSuspenseSpeed =
            Number.isFinite(this._suspenseCurrentSpeed as number) && (this._suspenseCurrentSpeed as number) > 0
                ? (this._suspenseCurrentSpeed as number)
                : this.getLastSpinSpeed() * ReelContainer.SUSPENSE_DECEL_TARGET_FRACTION
        const incomingSuspenseDuration = this.calculateDuration(stopDistance, incomingSuspenseSpeed)
        const suspenseDuration = Math.max(
            baseDuration * ReelContainer.SUSPENSE_END_SLOWDOWN_MULTIPLIER,
            incomingSuspenseDuration * ReelContainer.SUSPENSE_FINAL_STOP_MIN_RATIO,
            this._suspenseLastCycleDuration * ReelContainer.SUSPENSE_FINAL_STOP_MIN_RATIO
        )
        const duration = isSuspenseReel ? suspenseDuration : baseDuration
        const stopEasing = isSuspenseReel ? ReelContainer.SUSPENSE_END_EASING : this._speedSettings.LAST_EASING

        for (let index = 0; index < this.symbols.length; index++) {
            const isLastSymbol = index === this.symbols.length - 1
            const symbol = this.symbols[index]
            const extraLastDrop =
                isSuspenseReel && isLastSymbol ? ReelContainer.SUSPENSE_LAST_SYMBOL_EXTRA_DROP : 0
            const overshootShift = suspenseBounceShift + extraLastDrop

            gsap.to(symbol, {
                duration,
                y: (index - 1) * this._symbolHeight + overshootShift,
                ease: stopEasing,
                onComplete: () => {
                    if (isLastSymbol) {
                        this.onSymbolsLanded()
                    }

                    if (overshootShift) {
                        const tl = gsap.timeline()
                        tl.to(symbol, {
                            duration: isSuspenseReel
                                ? this._speedSettings.BOUNCING_BACK_TIME * ReelContainer.SUSPENSE_BOUNCE_BACK_TIME_MULTIPLIER
                                : this._speedSettings.BOUNCING_BACK_TIME,
                            y: `-=${overshootShift}`,
                            ease: isSuspenseReel
                                ? ReelContainer.SUSPENSE_BOUNCE_BACK_EASING
                                : this._speedSettings.BOUNCE_EASING,
                            onComplete: isLastSymbol ? onSpinStop : null,
                            onUpdate: () => {
                                if (tl.progress() >= 0.8) {
                                    this.updateSymbolsAfterStop()
                                }
                            },
                        })

                        const { EXTRA_SYMBOLS_TOP, EXTRA_SYMBOLS_BOTTOM } = Facade.reelsConfig
                        const symbolsCount = this._symbolsCount + EXTRA_SYMBOLS_TOP + EXTRA_SYMBOLS_BOTTOM

                        for (let i = symbolsCount; i < this.symbols.length; i++) {
                            this.symbols[i].visible = false
                        }

                        return
                    }

                    if (isLastSymbol) {
                        onSpinStop()
                    }
                },
            })
        }
    }

    protected onSymbolsLanded = () => {
        GameSignals.reelJustTouched.emit(this.index)
    }

    protected onSpinStop = () => {
        this.setState(this.reelStates.STOPPED)

        this._suspenseCurrentSpeed = null
        this._suspenseLastCycleDuration = 0
        if (this._suspenseDecelerationTween) {
            this._suspenseDecelerationTween.kill()
            this._suspenseDecelerationTween = null
        }

        const { EXTRA_SYMBOLS_TOP, EXTRA_SYMBOLS_BOTTOM } = Facade.reelsConfig
        const symbolsCount = this._symbolsCount + EXTRA_SYMBOLS_TOP + EXTRA_SYMBOLS_BOTTOM

        while (this.symbols.length > symbolsCount) {
            SymbolsPool.in(this.symbols.pop())
        }

        GameSignals.reelStopped.emit(this.index)

        if (this.index === Facade.reelsConfig.TOTAL_REELS - 1) {
            GameSignals.allReelsStopped.emit()
        }
    }

    public playScatterWaiting = (index: number) => {
        if (this.index !== index) return

        for (const symbol of this.symbols) {
            //@ts-ignore
            const currentAnimationName: string = symbol.animation && symbol.animation.state.tracks[0].animation.name

            if (symbol.index === SpecialSymbolType.SCATTER && currentAnimationName !== 'wait') {
                this.changeSymbolLayer(symbol, true)
                symbol.playAnimation('wait', true)
            }
        }
    }

    public playScatterTriggerAnimation = () => {
        for (const symbol of this.symbols) {
            if (symbol.index === SpecialSymbolType.SCATTER) {
                this.setSymbolLayer(symbol, SymbolLayerType.WINNING_SYMBOLS)
                symbol.playAnimation('win', false)
            }
        }
    }

    public stopScattersAnimation = () => {
        for (const symbol of this.symbols) {
            if (symbol.index === SpecialSymbolType.SCATTER && symbol.animation) {
                //TODO Should change this (&& symbol.animation)
                symbol.animation.state.addListener({
                    complete: (entry: ITrackEntry) => {
                        if (!symbol.animation) {
                            // TODO
                            return
                        }
                        symbol.animation.state.clearListeners()
                        symbol.stopAnimation()
                    },
                })
            }
        }
    }

    protected enableLayersBlur() {
        this._useBlurSymbolTextures = true

        for (const symbol of this.symbols) {
            symbol.enableBlurStaticTexture()
        }
    }

    protected disableLayersBlur() {
        this._useBlurSymbolTextures = false

        for (const symbol of this.symbols) {
            symbol.disableBlurStaticTexture()
        }
    }

    protected updateSymbolsAfterStop() {
        const { EXTRA_SYMBOLS_TOP, EXTRA_SYMBOLS_BOTTOM } = Facade.reelsConfig

        this.symbols.forEach((symbol: SymbolContainer, index: number) => {
            const isVisibleReelSymbol = index >= EXTRA_SYMBOLS_TOP && index < this.symbols.length - EXTRA_SYMBOLS_BOTTOM
            this.changeSymbolLayer(symbol, isVisibleReelSymbol)
            this.applyAnticipationDimmingToSymbol(symbol)
        })
    }

    protected updateSymbolAfterStop(symbol: SymbolContainer) {
        const { EXTRA_SYMBOLS_TOP, EXTRA_SYMBOLS_BOTTOM } = Facade.reelsConfig
        const symbolIndex = this.symbols.indexOf(symbol)

        if (symbolIndex === -1) {
            return
        }

        const isVisibleReelSymbol = symbolIndex >= EXTRA_SYMBOLS_TOP && symbolIndex < this.symbols.length - EXTRA_SYMBOLS_BOTTOM
        this.changeSymbolLayer(symbol, isVisibleReelSymbol)
        this.applyAnticipationDimmingToSymbol(symbol)
    }

    protected resetSymbols() {
        for (const symbol of this.symbols) {
            this.changeSymbolLayer(symbol)
            symbol.stopAnimation()
        }
    }

    public setState = (value: string) => {
        this.reelState = value
        Facade.reelsStates[this.index] = this.reelState
    }
}
